// =============================================================================
// USAGE TIER ALLOWANCE — the per-tier usage ceilings, as DATA (not code
// constants).
//
// docs/design/cost-governance/per-user-spend-caps-2026-07-07.md §3.2c. Mirrors
// rate_limit_policy.ts's own posture exactly: a small, singleton-per-key table
// (one row per tier), the Source falls back to an in-code default on ANY miss
// (no row, inactive row, DB error) and NEVER throws, so a missing/misconfigured
// row degrades to the STRICT in-code default, never to "unlimited."
//
// Two independent budgets per tier per period, because they meter two separate
// vendor costs: voice minutes (ElevenLabs Conversational AI, the dominant
// cost) and LLM cost (Claude, one call per turn). burst_per_min extends the
// existing per-user token-mint cap idea (agent-session.routes.ts's
// TOKEN_MINT_MAX_PER_WINDOW) into this table.
//
// All seeded numbers are PLACEHOLDERS (brief §3.2c) — not measured, carry no
// claim until Jason sets them against docs/design/unit-economics-2026-07-05.md
// (decision #4 in the brief).
//
// Additive only (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool,
// mirrored here, exported from index.ts. Nothing dropped or renamed.
// =============================================================================

import { boolean, index, integer, pgTable, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** The tier keys this brief seeds. A future tier is just a new row — no shape
 *  change — so this union is documentation, not an enum constraint (mirrors
 *  rate_limit_policy.ts's RateLimitRouteClass same posture). */
export type UsageTierKey = 'free' | 'trial' | 'paid';

export const usageTierAllowance = pgTable(
  'usage_tier_allowance',
  {
    id: id(),
    /** The tier key — 'free' | 'trial' | 'paid' today. UNIQUE so a re-seed/
     *  recalibration upserts in place (ON CONFLICT) instead of duplicating. */
    tier: varchar('tier', { length: 16 }).notNull(),
    /** Voice-minute ceiling (ElevenLabs Conversational AI) per period. */
    voiceMinutesPeriod: integer('voice_minutes_period').notNull(),
    /** Claude cost ceiling (micro-USD) per period. */
    llmCostMicroPeriod: integer('llm_cost_micro_period').notNull(),
    /** 'day' | 'month' — which period the two ceilings above measure over. */
    period: varchar('period', { length: 8 }).notNull().default('day'),
    /** Session mints/min per user — the belt for the per-user burst cap
     *  (brief §3.5), extending TOKEN_MINT_MAX_PER_WINDOW into DB-tunable data. */
    burstPerMin: integer('burst_per_min').notNull(),
    /** Row toggle — an inactive row is treated as absent, never silently
     *  applied (matches rate_limit_policy.active's exact posture). */
    active: boolean('active').notNull().default(true),
    ...timestamps,
  },
  (t) => ({
    tierIdx: index('usage_tier_allowance_tier_idx').on(t.tier),
    tierUq: uniqueIndex('usage_tier_allowance_tier_uq').on(t.tier),
  }),
);
